import "assets/scss/trends.scss";
import Filter from "components/trends/controlPanel/fitler";
import SearchableMultiSelect from "components/UI/Atoms/inputs/selects/searchableMultiselect";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { analyticsAPI } from "components/Apis/analytics";
import {
    addNewFilter,
    applyTrendsConfiguration,
    fetchFilterOptions,
    fetchAnnotationOptions,
    fetchGroupOptions,
    setGrouping,
    setOrdering,
    setSelectedAnnotations,
} from "redux/analytics/orderSlice";

const ACTIVE_TRENDS_CONFIG_STORAGE_KEY = "trends.active.config.v1";
const SAVED_TRENDS_CONFIGS_STORAGE_KEY = "trends.named.configs.v1";
const DEFAULT_RESTORE_VIEW_NAME = "Default: Completed by Delivery Date";
const getYesterdayDateString = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, "0");
    const day = String(yesterday.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const buildDefaultRestoreViewConfig = (filterOptionList = []) => {
    const deliveryDateOption =
        filterOptionList.find((option) => option.id === "delivery_date") ||
        { id: "delivery_date", label: "Delivery Date", type: "date" };
    const statusOption =
        filterOptionList.find((option) => option.id === "sylectus_status") ||
        {
            id: "sylectus_status",
            label: "Status",
            type: "select",
            options: [{ value: "Completed", label: "Completed" }],
        };

    return {
        engagedFilters: {
            default_delivery_date: {
                filteredEntity: deliveryDateOption,
                filterValue: { to: getYesterdayDateString() },
                filterOperator: "range",
            },
            default_sylectus_status_completed: {
                filteredEntity: statusOption,
                filterValue: { value: "Completed" },
                filterOperator: "exact",
            },
        },
        selectedAnnotations: [
            "order",
            "delivery_date",
            "pickup_location",
            "delivery_location",
            "customer",
            "revenue",
            "margin",
            "margin_percent",
            "service_team",
        ],
        selectedGroups: [],
        ordering: "-delivery_date",
        pageSize: 25,
    };
};

const safeParseJson = (raw, fallback) => {
    try {
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
};

const Trends = () => {
    const dispatch = useDispatch();
    const [exportFormat, setExportFormat] = useState("csv");
    const [exporting, setExporting] = useState(false);
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [nextPage, setNextPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingRows, setLoadingRows] = useState(false);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [savedConfigs, setSavedConfigs] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState("");
    const [configName, setConfigName] = useState("");
    const [isSaveNameOpen, setIsSaveNameOpen] = useState(false);
    const [configHydrated, setConfigHydrated] = useState(false);
    const loadMoreRef = useRef(null);
    const requestVersionRef = useRef(0);
    const loadingRowsRef = useRef(false);
    const hasHydratedConfigRef = useRef(false);
    const saveNamePopoverRef = useRef(null);
    const saveNameTriggerRef = useRef(null);

    const filterOptions = useSelector((state) => state.analytics.filterOptions);
    const groupOptions = useSelector((state) => state.analytics.groupOptions);
    const orderingOptions = useSelector((state) => state.analytics.orderingOptions);
    const annotationOptions = useSelector((state) => state.analytics.annotationOptions);
    const selectedAnnotations = useSelector((state) => state.analytics.selectedAnnotations);
    const pagination = useSelector((state) => state.analytics.pagination);

    // active configurations
    const engagedFilters = useSelector((state) => state.analytics.engagedFilters);
    const ordering = useSelector((state) => state.analytics.ordering);
    const selectedGroups = useMemo(
        () => (groupOptions.data || []).filter((opt) => opt.selected).map((opt) => opt.id),
        [groupOptions.data]
    );
    const annotationCount = selectedAnnotations.filter((id) => annotationOptions.data?.filter(opt => opt.id === id).length > 0).length;
    const activeFilterCount = Object.keys(engagedFilters || {}).length;

    useEffect(() => {
        // Logic to fetch data or handle side effects can be added here
        if (!filterOptions.data) {
            // dispatch action to fetch filter options
            dispatch(fetchFilterOptions());
        }
        if (!groupOptions.data) {
            // dispatch action to fetch group options
            dispatch(fetchGroupOptions());
        }
        if (!orderingOptions.data) {
            // dispatch action to fetch ordering options
        }
        if (!annotationOptions.data) {
            // dispatch action to fetch annotation options
            dispatch(fetchAnnotationOptions());
        }
    }, [dispatch, filterOptions.data, groupOptions.data, annotationOptions.data]);

    const optionsReady = !!(filterOptions.data && groupOptions.data && annotationOptions.data);
    const pageSize = pagination.pageSize || 25;
    const hasSelectedColumns = selectedGroups.length > 0 || selectedAnnotations.length > 0;

    const buildCurrentConfig = useCallback(() => ({
        engagedFilters,
        selectedAnnotations,
        selectedGroups,
        ordering,
        pageSize,
    }), [engagedFilters, selectedAnnotations, selectedGroups, ordering, pageSize]);

    const requestSignature = useMemo(() => {
        const normalizedFilters = Object.entries(engagedFilters || {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([id, filter]) => ({
                id,
                entity: filter.filteredEntity?.id || null,
                operator: filter.filterOperator || null,
                value: filter.filterValue || null,
            }));
        return JSON.stringify({
            pageSize: pagination.pageSize,
            filters: normalizedFilters,
            ordering: ordering || null,
            annotations: [...(selectedAnnotations || [])].sort(),
            groups: [...selectedGroups].sort(),
        });
    }, [engagedFilters, ordering, pagination.pageSize, selectedAnnotations, selectedGroups]);

    useEffect(() => {
        // ensure selected group is in selected annotations
        dispatch(fetchAnnotationOptions());
    }, [dispatch, JSON.stringify(selectedGroups)]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const saved = safeParseJson(localStorage.getItem(SAVED_TRENDS_CONFIGS_STORAGE_KEY), []);
        if (Array.isArray(saved)) {
            setSavedConfigs(saved);
        }
    }, []);

    useEffect(() => {
        if (!optionsReady) return;
        if (hasHydratedConfigRef.current) return;
        hasHydratedConfigRef.current = true;

        if (typeof window !== "undefined") {
            const activeConfig = safeParseJson(localStorage.getItem(ACTIVE_TRENDS_CONFIG_STORAGE_KEY), null);
            if (activeConfig && typeof activeConfig === "object") {
                dispatch(applyTrendsConfiguration(activeConfig));
            }
        }
        setConfigHydrated(true);
    }, [dispatch, optionsReady]);

    useEffect(() => {
        if (!configHydrated) return;
        if (typeof window === "undefined") return;
        localStorage.setItem(ACTIVE_TRENDS_CONFIG_STORAGE_KEY, JSON.stringify(buildCurrentConfig()));
    }, [buildCurrentConfig, configHydrated, requestSignature]);

    useEffect(() => {
        if (!isSaveNameOpen) return;

        const handleClickAway = (event) => {
            const target = event.target;
            if (saveNamePopoverRef.current?.contains(target)) return;
            if (saveNameTriggerRef.current?.contains(target)) return;
            setIsSaveNameOpen(false);
        };

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsSaveNameOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickAway);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickAway);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isSaveNameOpen]);

    const fetchPage = useCallback(async (pageToLoad, reset = false) => {
        if (!optionsReady) return;
        if (loadingRowsRef.current && !reset) return;
        if (!hasSelectedColumns) {
            setRows([]);
            setTotalCount(0);
            setHasMore(false);
            setInitialFetchDone(true);
            setFetchError("Select at least one column to view results.");
            return;
        }

        const version = requestVersionRef.current;
        loadingRowsRef.current = true;
        setLoadingRows(true);
        setFetchError(null);

        try {
            const response = await analyticsAPI.getData(
                pageToLoad,
                pageSize,
                Object.values(engagedFilters || {}),
                selectedGroups || [],
                selectedAnnotations || [],
                ordering || null
            );
            if (version !== requestVersionRef.current) return;

            const data = response.data || {};
            const results = data.results || [];
            const count = data.count ?? 0;
            const next = data.next;

            setTotalCount(count);
            setRows((prev) => (reset ? results : [...prev, ...results]));
            setHasMore(Boolean(next));
            setNextPage(pageToLoad + 1);
            setInitialFetchDone(true);
        } catch (error) {
            if (version !== requestVersionRef.current) return;
            setFetchError(error?.message || "Failed to load trends data");
            setHasMore(false);
            setInitialFetchDone(true);
        } finally {
            if (version === requestVersionRef.current) {
                loadingRowsRef.current = false;
                setLoadingRows(false);
            }
        }
    }, [
        engagedFilters,
        optionsReady,
        ordering,
        pageSize,
        selectedAnnotations,
        selectedGroups,
        hasSelectedColumns,
    ]);

    useEffect(() => {
        if (!optionsReady || !configHydrated) return;
        requestVersionRef.current += 1;
        setRows([]);
        setTotalCount(0);
        setHasMore(true);
        setNextPage(1);
        setInitialFetchDone(false);
        setFetchError(null);
        fetchPage(1, true);
    }, [configHydrated, fetchPage, optionsReady, requestSignature]);

    useEffect(() => {
        if (!optionsReady) return;
        if (!hasMore) return;
        if (!initialFetchDone) return;
        if (!loadMoreRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !loadingRows) {
                    fetchPage(nextPage, false);
                }
            },
            { root: null, rootMargin: "320px 0px", threshold: 0 }
        );

        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [fetchPage, hasMore, initialFetchDone, loadingRows, nextPage, optionsReady]);

    const annotationColumns = annotationOptions.data?.filter(opt => selectedAnnotations.includes(opt.id)).map(opt => ({
        header: opt.label,
        sublabel: opt.sublabel,
        accessor: opt.id,
        id: opt.id,
    })) || [];

    const groupColumns = selectedGroups.length > 1
        ? [{ header: "Grouping", accessor: "grouping", id: "grouping" }]
        : selectedGroups.map((groupId) => {
            const label = groupOptions.data?.find((opt) => opt.id === groupId)?.label || "Group";
            return { header: label, accessor: groupId, id: groupId };
        });

    const excludedColumnIds = new Set([...groupColumns.map((col) => col.id), ...selectedGroups]);
    const columns = [...groupColumns, ...annotationColumns.filter((col) => !excludedColumnIds.has(col.id))];

    const getGroupingText = (row) =>
        selectedGroups
            .map((groupId) => {
                const label = groupOptions.data?.find((opt) => opt.id === groupId)?.label || groupId;
                return `${label}: ${row?.[groupId] ?? "-"}`;
            })
            .join(" | ");

    const renderGroupingDisplay = (row) =>
        <div className="grouping-cell">
            {selectedGroups.length > 1 ? (
                <div className="multiple-groups">
                    {selectedGroups.map((groupId) => {
                        const label = groupOptions.data?.find((opt) => opt.id === groupId)?.label || "Group";
                        return (
                            <div key={groupId} className="grouping-subcell">
                                <span className="grouping-subcell__label">{label}: </span>
                                <span className="grouping-subcell__value">{row[groupId]}</span>
                            </div>
                        );
                    })}
                </div>
            ) : (
                row[selectedGroups[0]]
            )}
        </div>;

    const hasFilters = Object.keys(engagedFilters).length > 0;
    const maxExportCount = 2000;
    const isExportOverLimit = totalCount > maxExportCount;

    const buildCsv = (rows) => {
        const escape = (val) => {
            if (val === null || val === undefined) return '""';
            const str = String(val);
            return `"${str.replace(/"/g, '""')}"`;
        };
        const headerLine = columns.map((col) => escape(col.header)).join(",");
        const bodyLines = rows.map((row) =>
            columns.map((col) => escape(col.id === "grouping" ? getGroupingText(row) : row[col.accessor])).join(",")
        );
        return [headerLine, ...bodyLines].join("\n");
    };

    const buildExportRecords = (rows) =>
        rows.map((row) =>
            columns.reduce((acc, col) => {
                acc[col.header] = col.id === "grouping" ? getGroupingText(row) : row[col.accessor];
                return acc;
            }, {})
        );

    const handleExport = async () => {
        if (!columns.length) return;
        if (isExportOverLimit) return;
        setExporting(true);
        try {
            const pageSizeForExport = 500;
            let page = 1;
            let allRows = [];
            let total = null;

            while (true) {
                const response = await analyticsAPI.getData(
                    page,
                    pageSizeForExport,
                    Object.values(engagedFilters || {}),
                    selectedGroups || [],
                    selectedAnnotations || [],
                    ordering || null
                );
                const data = response.data || {};
                const results = data.results || [];
                total = data.count ?? total ?? results.length;
                allRows = allRows.concat(results);

                const fetchedEnough = total !== null && allRows.length >= total;
                const noMore = results.length < pageSizeForExport;
                if (fetchedEnough || noMore) break;
                page += 1;
            }

            const isExcel = exportFormat === "excel";
            const ext = isExcel ? "xlsx" : "csv";
            const blob = isExcel
                ? (() => {
                    const records = buildExportRecords(allRows);
                    const worksheet = XLSX.utils.json_to_sheet(records, { header: columns.map((col) => col.header) });
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, "Trends");
                    const wbArray = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
                    return new Blob(
                        [wbArray],
                        { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
                    );
                })()
                : new Blob([buildCsv(allRows)], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `trends-export-${Date.now()}.${ext}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Export failed", error);
        } finally {
            setExporting(false);
        }
    };

    const persistSavedConfigs = (nextConfigs) => {
        setSavedConfigs(nextConfigs);
        if (typeof window !== "undefined") {
            localStorage.setItem(SAVED_TRENDS_CONFIGS_STORAGE_KEY, JSON.stringify(nextConfigs));
        }
    };

    const handleSaveNamedConfig = (nameOverride) => {
        const trimmed = (nameOverride ?? configName).trim();
        if (!trimmed) return;

        const currentConfig = buildCurrentConfig();
        const existing = savedConfigs.find((cfg) => cfg.name.toLowerCase() === trimmed.toLowerCase());
        const now = Date.now();

        if (existing) {
            const updated = savedConfigs.map((cfg) => (
                cfg.id === existing.id
                    ? { ...cfg, name: trimmed, config: currentConfig, updatedAt: now }
                    : cfg
            ));
            persistSavedConfigs(updated);
            setSelectedConfigId(existing.id);
            setConfigName(trimmed);
            setIsSaveNameOpen(false);
            return;
        }

        const created = {
            id: `cfg_${now}`,
            name: trimmed,
            config: currentConfig,
            updatedAt: now,
        };
        const next = [created, ...savedConfigs];
        persistSavedConfigs(next);
        setSelectedConfigId(created.id);
        setConfigName(trimmed);
        setIsSaveNameOpen(false);
    };

    const handleDeleteNamedConfig = () => {
        if (!selectedConfigId) return;
        const next = savedConfigs.filter((cfg) => cfg.id !== selectedConfigId);
        persistSavedConfigs(next);
        setSelectedConfigId("");
        setIsSaveNameOpen(false);
    };

    const handleRestoreDefaultView = () => {
        dispatch(applyTrendsConfiguration(buildDefaultRestoreViewConfig(filterOptions.data || [])));
        setConfigName(DEFAULT_RESTORE_VIEW_NAME);
        setSelectedConfigId("");
        setIsSaveNameOpen(false);
    };

    return (
        <div className="trends-page trends-layout-v2">
            <aside className="trends-sidebar">
                <section className="trends-panel">
                    <header className="trends-panel__header">
                        <h4><i className="fas fa-columns" aria-hidden /> Columns</h4>
                        <button
                            type="button"
                            className="select-access-clear"
                            onClick={() => dispatch(setSelectedAnnotations([]))}
                            disabled={annotationCount === 0}
                        >
                            Clear
                        </button>
                    </header>
                    <SearchableMultiSelect
                        options={(annotationOptions.data || []).map((opt) => ({ value: opt.id, label: opt.label }))}
                        value={selectedAnnotations}
                        onChange={(vals) => {
                            const next = Array.isArray(vals) ? vals : vals ? [vals] : [];
                            dispatch(setSelectedAnnotations(next));
                        }}
                        placeholder="Pick columns"
                        maxMenuHeight={420}
                        chipLimit={6}
                    />
                </section>

                <section className="trends-panel">
                    <header className="trends-panel__header">
                        <h4><i className="fas fa-layer-group" aria-hidden /> Grouping</h4>
                        <button
                            type="button"
                            className="select-access-clear"
                            onClick={() => dispatch(setGrouping([]))}
                            disabled={selectedGroups.length === 0}
                        >
                            Clear
                        </button>
                    </header>
                    <SearchableMultiSelect
                        options={(groupOptions.data || []).map((opt) => ({ value: opt.id, label: opt.label }))}
                        value={selectedGroups}
                        onChange={(vals) => {
                            const next = Array.isArray(vals) ? vals : vals ? [vals] : [];
                            dispatch(setGrouping(next));
                        }}
                        placeholder="Group by one or more fields"
                        chipLimit={5}
                    />
                </section>

                <section className="trends-panel trends-panel--filters">
                    <header className="trends-panel__header">
                        <h4><i className="fas fa-filter" aria-hidden /> Filters</h4>
                        <div className="trends-panel__actions">
                            <button
                                type="button"
                                className="select-access-clear"
                                onClick={() => {
                                    dispatch(setSelectedAnnotations([]));
                                    dispatch(setGrouping([]));
                                }}
                                disabled={annotationCount === 0 && selectedGroups.length === 0}
                            >
                                Reset Controls
                            </button>
                            <button
                                className="add-filter-button"
                                onClick={() => dispatch(addNewFilter())}
                            >
                                <span className="add-filter-button__icon">+</span>
                                <span className="button-note">Add</span>
                            </button>
                        </div>
                    </header>
                    <div className="trends-panel__body">
                        {hasFilters ? (
                            Object.entries(engagedFilters).map(([filterId]) => (
                                <Filter key={filterId} filterId={filterId} />
                            ))
                        ) : (
                            <p className="c-section__placeholder">Add a filter to focus results.</p>
                        )}
                    </div>
                </section>
            </aside>

            <main className="trends-main">
                <section className="trends-views-strip">
                    <div className="trends-views-strip__label">
                        <i className="fas fa-bookmark" aria-hidden />
                        <span>Saved Views</span>
                    </div>
                    <div className="saved-configs saved-configs--inline">
                        <select
                            value={selectedConfigId}
                            onChange={(e) => {
                                const id = e.target.value;
                                setSelectedConfigId(id);
                                const selected = savedConfigs.find((cfg) => cfg.id === id);
                                if (selected) {
                                    setConfigName(selected.name);
                                    dispatch(applyTrendsConfiguration(selected.config));
                                    setIsSaveNameOpen(false);
                                }
                            }}
                        >
                            <option value="">Select saved view</option>
                            {savedConfigs.map((cfg) => (
                                <option key={cfg.id} value={cfg.id}>{cfg.name}</option>
                            ))}
                        </select>
                        <div className="saved-configs__actions">
                            <button
                                type="button"
                                className="select-access-clear"
                                onClick={handleRestoreDefaultView}
                            >
                                Restore Default
                            </button>
                            <button
                                type="button"
                                className="select-access-clear"
                                onClick={handleDeleteNamedConfig}
                                disabled={!selectedConfigId}
                            >
                                Delete
                            </button>
                            <button
                                ref={saveNameTriggerRef}
                                type="button"
                                className="select-access-clear"
                                onClick={() => setIsSaveNameOpen((prev) => !prev)}
                            >
                                Save View
                            </button>
                            {isSaveNameOpen && (
                                <div className="saved-configs__save-popover" ref={saveNamePopoverRef}>
                                    <form
                                        onSubmit={(event) => {
                                            event.preventDefault();
                                            handleSaveNamedConfig();
                                        }}
                                    >
                                        <input
                                            type="text"
                                            value={configName}
                                            onChange={(e) => setConfigName(e.target.value)}
                                            placeholder="View name"
                                            autoFocus
                                        />
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="trends-results">
                    <div className="trends-table-intro trends-table-intro--v2">
                        <h3 className="trends-table-heading"><i className="fas fa-table" aria-hidden />(F) Group and direct orders only</h3>
                        <div className="trends-results-actions">
                            <div className="trends-sidebar-meta">
                                <span><i className="fas fa-database" aria-hidden /> {totalCount || 0} total</span>
                                <span><i className="fas fa-list" aria-hidden /> {rows.length} loaded</span>
                                <span><i className="fas fa-sliders-h" aria-hidden /> {activeFilterCount} filters • {selectedGroups.length} groups • {annotationCount} columns</span>
                            </div>
                            <div className="trends-export-control">
                                <select
                                    className="trends-export-select"
                                    value={exportFormat}
                                    onChange={(e) => setExportFormat(e.target.value)}
                                >
                                    <option value="csv">CSV</option>
                                    <option value="excel">Excel (.xlsx)</option>
                                </select>
                                <button
                                    className="trends-export-button"
                                    type="button"
                                    onClick={handleExport}
                                    disabled={exporting || isExportOverLimit || (!rows.length && !totalCount)}
                                    title={isExportOverLimit ? `Export disabled for more than ${maxExportCount} rows` : undefined}
                                >
                                    {exporting ? <i className="fas fa-spinner fa-spin" aria-hidden></i> : <i className="fas fa-file-export" aria-hidden></i>}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="data-table-shell">
                        <table className="briefing-table all-orders-table customers-table">
                            <thead>
                                <tr>
                                    {hasSelectedColumns ? (
                                        columns.map((col) => (
                                            <th
                                                key={`header-${col.header}`}
                                                onClick={() => dispatch(setOrdering(col.id === "grouping" ? selectedGroups[0] : col.id))}
                                            >
                                                <div className="column-header">
                                                    <span className="column-header__title">{col.header}</span>
                                                    {col.sublabel && (
                                                        <span className="column-sublabel">{col.sublabel}</span>
                                                    )}
                                                    {(() => {
                                                        const sortField = col.id === "grouping" ? selectedGroups[0] : col.id;
                                                        if (ordering === sortField) return <span className="column-sort-indicator">▲</span>;
                                                        if (ordering === `-${sortField}`) return <span className="column-sort-indicator">▼</span>;
                                                        return null;
                                                    })()}
                                                </div>
                                            </th>
                                        ))
                                    ) : (
                                        <th>Columns</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {!hasSelectedColumns ? (
                                    <tr>
                                        <td>Select at least one column to view results.</td>
                                    </tr>
                                ) : rows.length > 0 ? (
                                    rows.map((item, index) => (
                                        <tr key={`row-${index}`}>
                                            {columns.map((col) => (
                                                <td key={col.accessor}>
                                                    {col.id === "grouping" ? renderGroupingDisplay(item) : item[col.accessor]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length || 1}>No data available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="infinite-status">
                        {loadingRows ? <span>Loading more rows...</span> : null}
                        {!loadingRows && fetchError ? <span className="infinite-error">{fetchError}</span> : null}
                    </div>
                    <div ref={loadMoreRef} className="infinite-sentinel" aria-hidden="true" />
                </section>
            </main>
        </div>
    );
}

export default Trends;
